import { Actions, PropGetters } from 'downshift';
import React, { useEffect, useRef} from 'react';
import styled, {
  css, keyframes,
} from 'styled-components';
import {
  rootFontSize,
  sourceSansPro,
} from '../../cssVariables';
import usePrevious from '../../tree/usePreviousHook';
import { Arrow } from '../graphControlDropdown';
import { smallMediaQuery } from '../Grid';
import {
  primaryColor2,
} from '../Utils';
import {
  VisualProps,
} from './Dropdown';
import { Option } from './query';

//#region Styling
// Calculate the fixed height of the prompt, in pixels. Need round pixels to
// avoid shifting layout when user clicks into placeholder:
const getPromptHeight = (visualProps: VisualProps) => Math.ceil(
  (visualProps.searchBarHeight -
    visualProps.placeholderMarginTop -
    visualProps.placeholderMarginBottom) * rootFontSize,
);

const PromptContainer = styled.div<{visualProps: VisualProps}>`
  display: grid;
  grid-template-columns: ${({visualProps}) => visualProps.magnifyingGlassWidth}rem 1fr;
  grid-column-gap: ${({visualProps}) => visualProps.magnifyingGlassGap}rem;
  margin-top: 2rem;
  cursor: pointer;
  color: ${({visualProps}) => visualProps.placeholderTextColor};
  background-color: ${({visualProps}) => visualProps.backgroundColor};

  /* Need integer pixels to avoid shifting layout when user clicks into placeholder: */
  height: ${({visualProps}) => getPromptHeight(visualProps)}px;

  @media ${smallMediaQuery} {
    margin-top: 0.8rem;
  }
`;
const MagnifyingGlassOuterContainer = styled.div`
  grid-column: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const MagnifyingGlassInnerContainer = styled.div<{visualProps: VisualProps}>`
  width: 100%;
  height: 0;
  padding-top: 100%;
  position: relative;

  svg {
    position: absolute;
    top: 0;
  }

  path {
    fill: ${({visualProps}) => visualProps.magnifyingGlassColor};
    opacity: 0.8;
  }
`;
const placeholderContentStyle = css`
  grid-column: 2;
  text-transform: uppercase;
  font-size: 1.5rem;
  line-height: 1.27;
  font-weight: 300;
  letter-spacing: 0.0375rem;

  @media ${smallMediaQuery} {
    font-size: 1.1rem;
  }
`;
const Input = styled.input<{visualProps: VisualProps}>`
  ${/* sc-declaration */ placeholderContentStyle}
  color: ${({visualProps}) => visualProps.placeholderTextColor};
  font-family: ${sourceSansPro}, sans-serif;
  border: none;
  padding: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;

  @media (min-width: 1200px) {
    font-size: ${({visualProps}) => visualProps.fontSize};
  }

  /* stylelint-disable-next-line property-no-vendor-prefix */
  -webkit-appearance: none;
  display: block;

  &::placeholder {
    color: ${({visualProps}) => visualProps.placeholderTextColor};
    opacity: 1;
  }

  &:focus {
    outline: none;
  }
`;
const InputContainer = styled.div`
  position: relative;
`;
const animation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;
const SpinnerContainer = styled.div`
  grid-column: 2;
  display: flex;
  align-items: center;
`;
// Taken from https://stephanwagner.me/only-css-loading-spinner
interface SpinnerProps {
  size: number;
  strokeWidth: number;
  strokeColor: string;
}
const Spinner = styled.div<SpinnerProps>`
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
  border-radius: 50%;
  border: ${props => props.strokeWidth}px solid white;
  border-top-color: ${props => props.strokeColor};
  animation: ${animation} .6s linear infinite;
`;

const ArrowContainer = styled.div<{visualProps: VisualProps}>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 2rem;
  pointer-events: none;
  background-color: ${({visualProps}) => visualProps.backgroundColor};

  svg {
    polyline {
      stroke: ${({visualProps}) => visualProps.magnifyingGlassColor};
    }
  }
`;

//#endregion

type Props = (
  {
    isLoading: true,
  } | {
    isLoading: false,
    isMenuOpen: true
    promptText: string
    getInputProps: PropGetters<Option>['getInputProps']
    inputKeydownHandler: (event: KeyboardEvent) => void,
  } | {
    isLoading: false,
    isMenuOpen: false,
    selectedItem: Option,
    openMenu: Actions<Option>['openMenu']
    getInputProps: PropGetters<Option>['getInputProps']
    inputKeydownHandler: (event: KeyboardEvent) => void,
  }
) & {
  visualProps: VisualProps,
};

const Prompt = (props: Props) => {
  const {visualProps} = props;

  // Need to create this "synthetic" value to account for when `isMenuOpen`
  // doesn't exist on props:
  const trackedIsMenuOpenValue = (props.isLoading === true) ? undefined : props.isMenuOpen;
  const prevTrackedIsMenuOpen = usePrevious(trackedIsMenuOpenValue);
  const inputElRef = useRef<HTMLInputElement | null>(null);
  const trackedInputKeyDownHandler = (props.isLoading === true) ? undefined : props.inputKeydownHandler;

  useEffect(() => {
    const {current: inputEl} = inputElRef;
    if (inputEl !== null && trackedInputKeyDownHandler !== undefined) {
      inputEl.addEventListener('keydown', trackedInputKeyDownHandler);
      return () => inputEl.removeEventListener('keydown', trackedInputKeyDownHandler);
    }
  }, [trackedInputKeyDownHandler]);

  useEffect(() => {
    const {current: inputEl} = inputElRef;
    if (inputEl !== null) {
      if (prevTrackedIsMenuOpen !== trackedIsMenuOpenValue) {
        if (trackedIsMenuOpenValue === false || trackedIsMenuOpenValue === undefined) {
          inputEl.blur();
        } else if (trackedIsMenuOpenValue === true) {
          inputEl.focus();
        }
      }
    }
  }, [trackedIsMenuOpenValue]);

  let mainContent: React.ReactElement<any>;
  if (props.isLoading === true) {
    mainContent = (
      <SpinnerContainer>
        <Spinner
          size={Math.round(getPromptHeight(visualProps) * .7)}
          strokeWidth={2}
          strokeColor={primaryColor2}
        />
      </SpinnerContainer>
    );
  } else {
    const {getInputProps} = props;
    if (props.isMenuOpen === true) {
      // Need to omit `as` from being passed on to `Input` to avoid type errors:
      const {as, ...restOfInputProps} = getInputProps({
        placeholder: props.promptText,
        type: 'text',
        ref: inputElRef,
      });
      mainContent = (
        <Input visualProps={visualProps} {...restOfInputProps}/>
      );

    } else {
      const {as, ...restOfInputProps} = getInputProps({
        placeholder: props.selectedItem.name,
        type: 'text',
        onClick: props.openMenu,
        ref: inputElRef,
        readonly: 'readonly',
        style: {
          cursor: 'pointer',
        },
      });
      mainContent = (
        <Input visualProps={visualProps} {...restOfInputProps}/>
      );
    }
  }

  return (
    <PromptContainer visualProps={visualProps}>
      <MagnifyingGlassOuterContainer>
        <MagnifyingGlassInnerContainer
          dangerouslySetInnerHTML={{__html: require('./countrySearch.svg')}}
          visualProps={visualProps}
        />
      </MagnifyingGlassOuterContainer>
      <InputContainer>
        {mainContent}
        <ArrowContainer visualProps={visualProps}>
          <Arrow />
        </ArrowContainer>
      </InputContainer>
    </PromptContainer>
  );
};

export default Prompt;
